import {
  ArrowRight,
  BookOpenCheck,
  CalendarCheck,
  CheckCircle2,
  ChevronLeft,
  GraduationCap,
  RotateCcw,
  Sparkles,
  Target,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  createStudentGrowthPlan,
  generateAiGrowthPlan,
  getLatestAiGrowthPlan,
  getLatestAiGrowthPlanGeneration,
  getCareerPlannerDirections,
  getKnowledgeMajors,
  getStudentGrowthMe,
} from '../api.js'
import { useAuth } from '../auth-context.js'
import { setPageMetadata } from '../page-metadata.js'

const questions = [
  {
    key: 'major',
    title: '你现在学习什么专业？',
    options: ['计算机科学与技术', '软件工程', '电子信息工程', '自动化', '电气工程及其自动化', '机械电子工程', '其他'],
  },
  {
    key: 'grade',
    title: '你现在处于哪个年级？',
    options: ['大一', '大二', '大三', '大四', '研究生', '已毕业'],
  },
  {
    key: 'goal',
    title: '你的学习目标是什么？',
    options: ['找实习', '做项目', '课程实训/作业', '提升基础', '准备面试', '考证/竞赛'],
  },
  {
    key: 'level',
    title: '你现在的基础到哪一步？',
    options: ['零基础', '学过基础语法', '做过小项目', '准备就业', '已有简历但项目弱'],
  },
  {
    key: 'dailyMinutes',
    title: '每天大概能投入多久？',
    options: ['30分钟', '1小时', '2小时', '3小时以上'],
  },
  {
    key: 'planDays',
    title: '这次计划想做多长？',
    options: ['7天', '30天', '60天', '120天'],
  },
  {
    key: 'direction',
    title: '你想学习哪个方向？',
    options: ['Java后端', 'Python', '前端', '数据分析', 'AI入门', '其他'],
  },
  {
    key: 'painPoint',
    title: '当前最大问题是什么？',
    options: ['不知道学什么', '看得懂但写不出来', '没有项目', '简历没内容', '面试不会说', '作业/实训不会做'],
  },
]

const minutesMap = {
  '30分钟': 30,
  '1小时': 60,
  '2小时': 120,
  '3小时以上': 180,
}

const dayMap = {
  '7天': 7,
  '30天': 30,
  '60天': 60,
  '120天': 120,
}

const directionBase = {
  Java后端: {
    title: 'Java 后端开发路线',
    foundations: ['Java 环境与基础语法', '流程控制、方法拆分与集合入门', '面向对象、异常与文件读写'],
    engineering: ['Spring Boot 接口开发', 'MySQL 表设计与 CRUD', '登录鉴权、参数校验与异常处理'],
    project: '校园二手交易系统',
    practice: ['Java 基础语法练习', 'Spring REST API 小练习', 'SQL 查询与分页练习'],
  },
  Python: {
    title: 'Python 应用与自动化路线',
    foundations: ['Python 语法、函数与模块', '文件处理、异常与常用库', '数据结构与脚本拆分'],
    engineering: ['接口调用与数据清洗', 'Flask/FastAPI 小接口', '自动化脚本与结果报告'],
    project: '校园数据整理与自动报表工具',
    practice: ['Python 基础语法练习', '文件处理练习', '接口数据解析练习'],
  },
  前端: {
    title: '前端开发与作品路线',
    foundations: ['HTML/CSS 页面结构', 'JavaScript 基础与 DOM', '组件化和状态管理入门'],
    engineering: ['React 页面开发', '接口联调与表单校验', '移动端适配与部署'],
    project: '学生任务看板与作品集页面',
    practice: ['JS 基础练习', 'React 组件练习', '响应式布局练习'],
  },
  数据分析: {
    title: '数据分析与可视化路线',
    foundations: ['Excel/SQL 指标理解', 'Python 数据处理基础', '数据清洗与描述统计'],
    engineering: ['业务指标拆解', '可视化看板制作', '分析报告表达'],
    project: '校园消费或学习行为分析报告',
    practice: ['SQL 查询练习', 'Pandas 清洗练习', '图表解读练习'],
  },
  AI入门: {
    title: 'AI 入门与应用路线',
    foundations: ['Python 基础', 'AI 概念、Prompt 与模型调用', '数据集与评估基础'],
    engineering: ['模型 API 调用', 'RAG/知识库入门', 'AI 应用 Demo 打磨'],
    project: '课程资料智能问答助手',
    practice: ['Python 函数练习', 'Prompt 改写练习', '模型调用练习'],
  },
  其他: {
    title: '通用学习成长路线',
    foundations: ['确认目标岗位或课程交付物', '补齐核心基础概念', '完成一个可展示小成果'],
    engineering: ['拆解任务清单', '持续输出作品材料', '整理复盘和表达'],
    project: '个人学习成果展示包',
    practice: ['基础概念练习', '任务拆解练习', '复盘表达练习'],
  },
}

function parseDays(value) {
  return dayMap[value] || 30
}

function parseMinutes(value) {
  return minutesMap[value] || 60
}

function taskLoad(minutes) {
  if (minutes <= 30) return { dailyCount: 2, label: '轻量任务' }
  if (minutes <= 60) return { dailyCount: 3, label: '标准任务' }
  if (minutes <= 120) return { dailyCount: 4, label: '强化任务' }
  return { dailyCount: 5, label: '冲刺任务' }
}

function buildPlan(profile) {
  const base = directionBase[profile.direction] || directionBase.其他
  const days = parseDays(profile.planDays)
  const minutes = parseMinutes(profile.dailyMinutes)
  const load = taskLoad(minutes)
  const isInternship = profile.goal === '找实习' || profile.goal === '准备面试'
  const isCourse = profile.goal === '课程实训/作业'
  const stages = [
    {
      title: profile.level === '零基础' ? '基础概念与环境准备' : '快速复习基础',
      goal: '先建立可执行的学习起点。',
      tasks: base.foundations,
    },
    {
      title: '练习与小项目启动',
      goal: '把看得懂的内容转成能写出来的成果。',
      tasks: [...base.practice.slice(0, 2), `${base.project}：完成最小功能`],
    },
    {
      title: isInternship ? '实习/面试冲刺' : isCourse ? '课程交付物拆解' : '项目优化与表达',
      goal: '沉淀项目、简历和复盘材料。',
      tasks: isInternship
        ? ['准备项目讲解稿', '整理简历关键词', '完成一次面试题复盘']
        : isCourse
          ? ['确认需求和功能清单', '推进一个功能模块', '整理报告和截图']
          : base.engineering,
    },
  ]

  const todayTasks = [
    '确认今天要完成的一个学习节点',
    '完成一道练习或一个小功能',
    '记录今天的卡点和下一步',
  ].slice(0, load.dailyCount)

  return {
    directionTitle: base.title,
    loadLabel: load.label,
    dailyCount: load.dailyCount,
    days,
    minutes,
    stages,
    todayTasks,
    projects: [base.project, isCourse ? '课程报告与答辩材料包' : '个人作品集页面'],
    practices: base.practice,
    careerAdvice: isInternship
      ? ['每周整理一次项目复盘', '准备 1 分钟项目介绍', '把技术点改成“问题-行动-结果”表达']
      : ['把每周成果沉淀成 README、截图或学习笔记', '项目完成后再补简历表达'],
  }
}

function OptionButton({ active, children, onClick }) {
  return (
    <button className={active ? 'is-active' : ''} type="button" onClick={onClick}>
      {active ? <CheckCircle2 size={18} /> : <span className="onboarding-dot" />}
      {children}
    </button>
  )
}

function StudentOnboardingPage() {
  const auth = useAuth()
  const navigate = useNavigate()
  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState({
    major: '',
    grade: '',
    goal: '',
    level: '',
    dailyMinutes: '',
    planDays: '',
    direction: '',
    painPoint: '',
  })
  const [existingPlan, setExistingPlan] = useState(null)
  const [checkingPlan, setCheckingPlan] = useState(false)
  const [regenerateMode, setRegenerateMode] = useState(false)
  const [saving, setSaving] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [aiPlan, setAiPlan] = useState(null)
  const [catalogs, setCatalogs] = useState({ majors: [], careers: [] })
  const [saveError, setSaveError] = useState('')
  const isLoggedIn = Boolean(auth?.user)

  useEffect(() => {
    setPageMetadata({
      title: '30秒生成学习计划 - 青途 AI',
      description: '通过轻量诊断，按学习目标、基础、时间和方向生成更贴合学生水平的学习计划。',
    })
  }, [])

  useEffect(() => {
    if (auth?.loading || !isLoggedIn) return undefined
    const controller = new AbortController()
    setCheckingPlan(true)
    getStudentGrowthMe(controller.signal)
      .then((payload) => setExistingPlan(payload?.plan || null))
      .catch(() => setExistingPlan(null))
      .finally(() => setCheckingPlan(false))
    return () => controller.abort()
  }, [auth?.loading, isLoggedIn])

  useEffect(() => {
    if (auth?.loading || !isLoggedIn) return undefined
    let timer; let observedActive = false; let cancelled = false
    const poll = async () => {
      try {
        const result = await getLatestAiGrowthPlanGeneration()
        const status = result?.generation?.status
        if (status === 'generating' || status === 'validating') {
          observedActive = true
          setGenerating(true)
          setSaveError(status === 'validating' ? 'AI 已生成初稿，正在进行专业性校验…' : 'AI 正在读取画像和知识库，请稍候…')
          timer = window.setTimeout(poll, 1800)
        } else if (observedActive && status === 'completed') {
          const latest = await getLatestAiGrowthPlan()
          if (!cancelled) {
            setAiPlan(latest?.plan || null)
            setGenerating(false)
            setSaveError('')
            setStep(questions.length)
          }
        } else if (observedActive && status === 'failed') {
          setGenerating(false)
          setSaveError(result?.generation?.errorMessage || '专属路线生成失败，请重新生成。')
        }
      } catch {
        if (!cancelled && observedActive) timer = window.setTimeout(poll, 2500)
      }
    }
    poll()
    return () => { cancelled = true; if (timer) window.clearTimeout(timer) }
  }, [auth?.loading, isLoggedIn])

  useEffect(() => {
    const controller = new AbortController()
    Promise.all([
      getKnowledgeMajors(controller.signal).catch(() => ({ majors: [] })),
      getCareerPlannerDirections(controller.signal).catch(() => []),
    ]).then(([majorPayload, careers]) => {
      setCatalogs({
        majors: Array.isArray(majorPayload?.majors) ? majorPayload.majors : [],
        careers: Array.isArray(careers) ? careers : [],
      })
    })
    return () => controller.abort()
  }, [])

  const current = questions[step]
  const isResult = step >= questions.length
  const plan = useMemo(() => buildPlan(answers), [answers])
  const progress = isResult ? 100 : Math.round((step / questions.length) * 100)
  const canContinue = current ? Boolean(answers[current.key]) : false
  const shouldShowExisting = existingPlan && !regenerateMode && !isResult

  function choose(key, value) {
    setSaveError('')
    setAnswers((currentAnswers) => ({ ...currentAnswers, [key]: value }))
  }

  function nextStep() {
    if (!canContinue) return
    setStep((value) => value + 1)
  }

  function startRegenerate() {
    setRegenerateMode(true)
    setAiPlan(null)
    setStep(0)
    setSaveError('')
  }

  function findCatalogItem(items, value, fields) {
    const needle = String(value || '').replace(/\s+/g, '').toLowerCase()
    return items.find((item) => fields.some((field) => {
      const candidate = String(item?.[field] || '').replace(/\s+/g, '').toLowerCase()
      return candidate === needle || candidate.includes(needle) || needle.includes(candidate)
    })) || null
  }

  async function generatePlan() {
    if (!canContinue || generating) return
    if (!isLoggedIn) {
      setSaveError('登录后才能调用 AI 生成并保存专属路线。')
      return
    }
    setGenerating(true)
    setSaveError('')
    try {
      const major = findCatalogItem(catalogs.majors, answers.major, ['majorName', 'name'])
      const career = findCatalogItem(catalogs.careers, answers.direction, ['name', 'key'])
      const result = await generateAiGrowthPlan({
        majorId: major?.id || null,
        careerId: career?.id || null,
        careerKey: career?.key || '',
        direction: answers.direction,
        grade: answers.grade,
        level: answers.level,
        goal: answers.goal,
        durationDays: parseDays(answers.planDays),
        dailyMinutes: parseMinutes(answers.dailyMinutes),
        interest: answers.painPoint ? [answers.painPoint] : [],
        currentSkills: [],
      })
      setAiPlan(result?.plan || null)
      setStep(questions.length)
    } catch (error) {
      setSaveError(error?.message || '专属路线生成失败，请检查所选专业和岗位后重试。')
    } finally {
      setGenerating(false)
    }
  }

  async function savePlan() {
    setSaveError('')
    if (!isLoggedIn) {
      setSaveError('登录后才能保存为你的学习计划。你可以先查看结果，登录后再回来保存。')
      return
    }
    setSaving(true)
    try {
      const profile = {
        major: answers.major,
        grade: answers.grade,
        goal: answers.goal,
        level: answers.level,
        dailyMinutes: parseMinutes(answers.dailyMinutes),
        planDays: parseDays(answers.planDays),
        direction: answers.direction,
        painPoint: answers.painPoint,
      }
      await createStudentGrowthPlan({
        targetDirection: plan.directionTitle,
        stage: `${answers.level} / ${answers.goal}`,
        roadmap: plan.stages.map((stage) => `${stage.title}：${stage.goal}`),
        skills: plan.stages.flatMap((stage) => stage.tasks),
        materials: {
          diagnostic: {
            ...answers,
            ...profile,
            dailyTimeLabel: answers.dailyMinutes,
            planDaysLabel: answers.planDays,
          },
          stages: plan.stages,
          todayTasks: plan.todayTasks,
          recommendedProjects: plan.projects,
          recommendedPractices: plan.practices,
          careerAdvice: plan.careerAdvice,
          summary: {
            loadLabel: plan.loadLabel,
            dailyCount: plan.dailyCount,
          },
        },
        diagnostic: profile,
      })
      navigate('/learning')
    } catch (error) {
      setSaveError(error?.message || '保存学习计划失败，请稍后重试。')
    } finally {
      setSaving(false)
    }
  }

  return (
    <main className="page-container student-onboarding-page">
      <section className="student-onboarding-shell">
        <div className="student-onboarding-top">
          <span><Sparkles size={16} /> 30秒生成学习计划</span>
          <div className="student-onboarding-progress" aria-label={`进度 ${progress}%`}>
            <i style={{ width: `${progress}%` }} />
          </div>
        </div>

        {shouldShowExisting ? (
          <section className="student-onboarding-result">
            <span><GraduationCap size={18} /> 已有学习计划</span>
            <h1>{existingPlan.targetDirection || '我的学习计划'}</h1>
            <p>你已经有一份学习计划，可以继续当前计划，也可以重新生成。</p>
            <div className="student-onboarding-result-grid">
              <article>
                <Target size={20} />
                <h2>当前路线</h2>
                <strong>{existingPlan.targetDirection || '学习路线'}</strong>
                <p>{existingPlan.stage || '当前阶段'}</p>
              </article>
              <article>
                <CalendarCheck size={20} />
                <h2>当前进度</h2>
                <strong>{existingPlan.progress || 0}%</strong>
                <p>历史成长记录会继续保留。</p>
              </article>
            </div>
            <div className="student-onboarding-actions">
              <Link className="primary-button" to="/learning">
                继续当前计划 <ArrowRight size={16} />
              </Link>
              <button className="secondary-button" type="button" onClick={startRegenerate}>
                <RotateCcw size={16} /> 重新生成计划
              </button>
            </div>
          </section>
        ) : !isResult ? (
          <section className="student-onboarding-question">
            <small>{checkingPlan ? '正在检查已有计划...' : `第 ${step + 1} / ${questions.length} 题`}</small>
            <h1>{current.title}</h1>
            <div className="student-onboarding-options">
              {current.options.map((option) => (
                <OptionButton
                  active={answers[current.key] === option}
                  key={option}
                  onClick={() => choose(current.key, option)}
                >
                  {option}
                </OptionButton>
              ))}
            </div>

            <div className="student-onboarding-nav">
              <button className="secondary-button" type="button" disabled={step === 0} onClick={() => setStep((value) => value - 1)}>
                <ChevronLeft size={16} /> 上一步
              </button>
              <button
                className="primary-button"
                type="button"
                disabled={!canContinue || generating}
                onClick={step === questions.length - 1 ? generatePlan : nextStep}
              >
                {generating ? 'AI 正在分析...' : step === questions.length - 1 ? '生成我的成长方案' : '下一步'} <ArrowRight size={16} />
              </button>
            </div>
          </section>
        ) : (
          <section className="student-onboarding-result">
            <span><GraduationCap size={18} /> {aiPlan ? 'AI 成长方案已生成' : '基础学习计划已生成'}</span>
            <h1>{aiPlan?.target || plan.directionTitle}</h1>
            <p>{aiPlan?.overview || '根据你的目标、基础、时间和最大问题，先生成一版可执行的基础计划。'}</p>

            <div className="student-onboarding-profile-summary">
              <span>当前目标：{answers.goal}</span>
              <span>当前基础：{answers.level}</span>
              <span>每天学习时间：{answers.dailyMinutes}</span>
              <span>总周期：{answers.planDays}</span>
              <span>任务强度：{plan.loadLabel}</span>
            </div>

            <div className="student-onboarding-result-grid">
              <article className="is-wide">
                <BookOpenCheck size={20} />
                <h2>阶段路线</h2>
                <div className="student-onboarding-stage-list">
                  {(aiPlan?.stages || plan.stages).map((stage, index) => (
                    <section key={stage.id || stage.name || stage.title}>
                      <strong>{index + 1}. {stage.name || stage.title}{stage.durationDays ? ` · ${stage.durationDays} 天` : stage.duration ? ` · ${stage.duration}` : ''}</strong>
                      <p>{stage.stageGoal || stage.objectives?.join('；') || stage.goal}</p>
                      <div className="student-onboarding-route">
                        {(stage.nodes || stage.tasks || []).map((item) => <span key={item.id || item}>{item.title || item}</span>)}
                      </div>
                    </section>
                  ))}
                </div>
              </article>
              <article>
                <CalendarCheck size={20} />
                <h2>今日任务</h2>
                {plan.todayTasks.map((item) => <p key={item}>· {item}</p>)}
              </article>
              <article>
                <Target size={20} />
                <h2>推荐项目</h2>
                {(aiPlan?.projects || plan.projects).map((item) => <p key={item.id || item}>· {item.name || item}{item.reason ? `：${item.reason}` : ''}</p>)}
              </article>
              <article>
                <CheckCircle2 size={20} />
                <h2>推荐练习</h2>
                {(aiPlan?.competitions || plan.practices).map((item) => <p key={item.id || item}>· {item.name || item}{item.reason ? `：${item.reason}` : ''}</p>)}
              </article>
              <article>
                <Sparkles size={20} />
                <h2>{aiPlan ? '推荐证书' : '简历/面试建议'}</h2>
                {(aiPlan?.certificates || plan.careerAdvice).map((item) => <p key={item.id || item}>· {item.name || item}{item.reason ? `：${item.reason}` : ''}</p>)}
              </article>
            </div>

            {saveError ? <p className="student-onboarding-error">{saveError}</p> : null}

            <div className="student-onboarding-actions">
              {aiPlan ? (
                <button className="primary-button" type="button" onClick={() => navigate('/growth')}>
                  查看我的成长方案 <ArrowRight size={16} />
                </button>
              ) : (
                <button className="primary-button" type="button" onClick={savePlan} disabled={saving}>
                  {saving ? '正在保存...' : existingPlan ? '覆盖推荐路线并保存' : '保存为我的学习计划'} <ArrowRight size={16} />
                </button>
              )}
              {!isLoggedIn ? (
                <Link className="secondary-button" to="/login">登录后保存</Link>
              ) : null}
              <button className="secondary-button" type="button" onClick={() => { setAiPlan(null); setStep(0) }}>
                {aiPlan ? '重新生成' : '重新填写'}
              </button>
              <button className="secondary-button" type="button" onClick={() => { setAiPlan(null); setStep(0) }}>调整目标</button>
              <button className="secondary-button" type="button" onClick={() => { setAiPlan(null); setStep(questions.findIndex((item) => item.key === 'dailyMinutes')) }}>修改每日时间</button>
            </div>
          </section>
        )}
      </section>
    </main>
  )
}

export default StudentOnboardingPage
